<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { fetchAccountProductsConfig, sampleCreateProductsForAccount } from '@/api/accountsAdmin'
import { useAccountStore } from '@/stores/accountStore'

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const accountStore = useAccountStore()

const accountName = route?.params?.account
const productConfigName = route?.query?.productName
const productPath = route.query.productName

const config = ref<Record<string, any>>({})
const productFriendlyName = ref('')
const sampleObj = ref<Record<string, any>>({})
const commonData = ref({})
const formData = reactive<Record<string, any>>({})
const validationErrors = ref<string[]>([])

const snackbar = ref({
  show: false,
  message: '' as string,
  color: 'error' as 'error' | 'success' | 'info',
})

const loadProductConfig = async () => {
  try {
    const configProduct = route.query.productName
    const data = await fetchAccountProductsConfig(configProduct as string)
    config.value = data.config
    productFriendlyName.value =
      data?.commonData?.productFriendlyName ||
      data?.config?.productFriendlyName ||
      data?.config?.productName ||
      ''
    sampleObj.value = data.sampleObj
    initializeFormData(data.sampleObj)
    commonData.value = data.commonData
  } catch (error) {
    console.error('Failed to load product config:', error)
  }
}

const initializeFormData = (sample: Record<string, any>) => {
  Object.keys(sample).forEach((key) => {
    if (Array.isArray(sample[key])) {
      formData[key] = sample[key].map((item: any) =>
        typeof item === 'object' ? reactive(item) : typeof item === 'number' ? item : item
      )
    } else if (typeof sample[key] === 'object') {
      formData[key] = reactive({ ...sample[key] })
    } else {
      formData[key] = sample[key]
    }
  })
}

const isFieldRequired = (key: string, subKey?: string): boolean => {
  if (subKey) {
    return config.value.properties[key]?.items?.required?.includes(subKey) ?? false
  }
  return config.value.required?.includes(key) ?? false
}

const hasRequiredNestedFields = (key: string): boolean => {
  return (config.value.properties[key]?.required ?? []).length > 0
}

const getRequiredNestedFields = (key: string): string[] => {
  return config.value.properties[key]?.required ?? []
}

const validateForm = (): boolean => {
  validationErrors.value = []

  for (const key of config.value.required || []) {
    const value = formData[key]

    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      validationErrors.value.push(`Field "${key}" is required.`)
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      for (const subKey of config.value.properties[key]?.required || []) {
        if (value[subKey] === undefined || value[subKey] === null || value[subKey] === '') {
          validationErrors.value.push(`Field "${key}.${subKey}" is required.`)
        }
      }
    }
  }

  return validationErrors.value.length === 0
}
const addArrayItem = (key: string) => {
  if (Array.isArray(formData[key])) {
    formData[key].push(config.value?.properties[key]?.items?.type === 'object' ? {} : '')
  }
}

const removeArrayItem = (key: string, index: number) => {
  if (Array.isArray(formData[key])) {
    formData[key].splice(index, 1)
  }
}
function generateRandomId(length = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

const submitForm = async () => {
  if (!validateForm()) return

  if (productConfigName === 'coupon_builder') {
    ;(formData as any).couponId = generateRandomId()
  }
  const objToSend = {
    accountName,
    productName: productConfigName, 
    config: JSON.parse(JSON.stringify(formData)),
    commonData: commonData.value,
  }
  try {
    loading.value = true
    await sampleCreateProductsForAccount(objToSend)

    snackbar.value.message = 'Product for Account created successfully!'
    snackbar.value.color = 'success'
    snackbar.value.show = true
    setTimeout(() => {
      try {
        accountStore.setProductsCache([])
      } catch {}
      router.push(`/account/products/${accountName}`)
    }, 1200)
    loading.value = false
  } catch (error) {
    console.error('Error creating Product:', error)
    alert((error as any).response?.data)
    loading.value = false
  }
}

const onNumberInput = (key: string) => {
  const value = formData[key]
  if (value !== '' && !isNaN(value)) {
    formData[key] = Number(value)
  } else {
    formData[key] = ''
  }
}
const onNumberInput2 = (key: string, index: number) => {
  const value = formData[key][index]
  if (typeof value === 'string') {
    const parsedValue = parseFloat(value)
    if (!isNaN(parsedValue)) {
      formData[key][index] = parsedValue
    } else {
      formData[key][index] = ''
    }
  }
}
const enforceNonEmptyNonNegativeNumber = (key: string, index: number, subKey: string) => {
  let value = formData[key][index][subKey]
  if (typeof value === 'string') {
    value = parseFloat(value)
  }
  if (value === '' || value === null || isNaN(value)) {
    formData[key][index][subKey] = 0
  }
  if (value < 0) {
    formData[key][index][subKey] = 0
  }
}
onMounted(loadProductConfig)
</script>

<template>
  <v-container>
    <div class="mx-auto p-4">
      <h2 class="text-h5 font-weight-medium mb-4">
        Add
        <span class="text-secondary">{{ productFriendlyName || productConfigName }}</span>
        Product Confirmation
      </h2>
      <form @submit.prevent="submitForm" v-if="Object.keys(sampleObj).length">
        <div v-for="(value, key) in sampleObj" :key="key" class="mb-6">
          <div v-if="Array.isArray(value)">
            <label class="block text-lg font-medium mb-2">
              {{ key }}
              <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
            </label>
            <p v-if="hasRequiredNestedFields(key)" class="text-gray-500 text-sm">
              This array contains required fields:
              {{ getRequiredNestedFields(key).join(', ') }}
            </p>
            <div v-if="typeof value[0] === 'string'">
              <div
                v-for="(item, index) in formData[key]"
                :key="index"
                class="flex flex-col gap-2 mb-2"
              >
                <v-text-field
                  v-model="formData[key][index]"
                  :label="`Item ${index + 1}`"
                  type="text"
                  outlined
                  dense
                />
                <v-btn icon color="error" @click="removeArrayItem(key, index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else-if="typeof value[0] === 'number'">
              <div
                v-for="(item, index) in formData[key]"
                :key="index"
                class="flex flex-col gap-2 mb-2"
              >
                <v-text-field
                  v-model="formData[key][index]"
                  :label="`Item ${index + 1}`"
                  type="number"
                  outlined
                  dense
                  @input="onNumberInput2(key, index)"
                />
                <v-btn icon color="error" @click="removeArrayItem(key, index)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div v-else-if="typeof value[0] === 'object'">
              <div
                v-for="(item, index) in formData[key]"
                :key="index"
                class="flex flex-col gap-2 mb-2"
              >
                <div v-for="(subValue, subKey) in item" :key="subKey" class="flex flex-col gap-2">
                  <label class="block text-sm font-medium">
                    {{ subKey }}
                    <span v-if="isFieldRequired(key, subKey)" class="text-red-500">*</span>
                  </label>
                  <v-select
                    v-if="typeof subValue === 'boolean'"
                    v-model="formData[key][index][subKey]"
                    :items="[true, false]"
                    :item-title="(item) => (item ? 'True' : 'False')"
                    :item-value="(item) => item"
                    label="Select True/False"
                    outlined
                    dense
                  />
                  <v-text-field
                    v-else-if="typeof subValue === 'number'"
                    v-model.number="formData[key][index][subKey]"
                    type="number"
                    outlined
                    dense
                    @blur="enforceNonEmptyNonNegativeNumber(key, index, subKey)"
                  />
                  <v-text-field v-else v-model="formData[key][index][subKey]" outlined dense />
                </div>
              </div>
            </div>
            <v-btn
              v-if="config?.properties[key]?.items?.type !== 'object'"
              type="button"
              @click="addArrayItem(key)"
              class="mt-2"
            >
              Add Item
            </v-btn>
          </div>

          <div v-else-if="typeof value === 'string'">
            <label class="block text-lg font-medium mb-2">
              {{ key }}
              <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
            </label>
            <v-text-field v-model="formData[key]" outlined dense />
          </div>

          <div v-else-if="typeof value === 'number'">
            <label class="block text-lg font-medium mb-2">
              {{ key }}
              <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
            </label>
            <v-text-field
              v-model="formData[key]"
              type="number"
              outlined
              dense
              @input="onNumberInput(key)"
            />
          </div>

          <div v-else-if="key !== 'Enable' && typeof value === 'boolean'">
            <label class="block text-lg font-medium mb-2">
              {{ key }}
              <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
            </label>
            <v-select
              v-model="formData[key]"
              :items="[true, false]"
              :item-title="(item) => (item ? 'True' : 'False')"
              :item-value="(item) => item"
              outlined
              dense
            />
          </div>

          <div v-else-if="typeof value === 'object'">
            <label class="block text-lg font-medium mb-2">
              {{ key }}
              <span v-if="isFieldRequired(key)" class="text-red-500">*</span>
            </label>
            <p v-if="hasRequiredNestedFields(key)" class="text-gray-500 text-sm">
              This object contains required fields:
              {{ getRequiredNestedFields(key).join(', ') }}
            </p>

            <div class="grid gap-2">
              <div v-for="(subValue, subKey) in value" :key="subKey" class="flex flex-col gap-2">
                <label class="block text-sm font-medium">
                  {{ subKey }}
                  <span v-if="isFieldRequired(key, subKey)" class="text-red-500">*</span>
                </label>
                <v-select
                  v-if="typeof subValue === 'boolean'"
                  v-model="formData[key][subKey]"
                  :items="[true, false]"
                  :item-title="(item) => (item ? 'True' : 'False')"
                  :item-value="(item) => item"
                  label="Select True/False"
                  outlined
                  dense
                />
                <v-text-field
                  v-else-if="typeof subValue === 'number'"
                  v-model.number="formData[key][subKey]"
                  type="number"
                  outlined
                  dense
                />
                <v-text-field v-else v-model="formData[key][subKey]" outlined dense />
              </div>
            </div>
          </div>
        </div>

        <div v-if="validationErrors.length" class="text-red-500 alert-box mt-4">
          <p style="font-size: 20px; margin-bottom: 10px">Please fill the following fields:</p>
          <ul>
            <li v-for="error in validationErrors" :key="error">{{ error }}</li>
          </ul>
        </div>

        <div v-for="(value, key) in commonData" :key="key" class="mb-4">
          <label class="block text-lg font-medium mb-2">
            {{ key }}
          </label>
          <v-text-field
            v-model="(commonData as any)[key]"
            disabled
            :placeholder="key"
            outlined
            dense
          />
        </div>

        <v-sheet class="sticky-actions d-flex align-center" elevation="2">
          <v-spacer></v-spacer>
          <v-btn variant="text" color="grey-darken-1" class="mr-2" @click="router.back()">
            Cancel
          </v-btn>
          <v-btn
            type="submit"
            :disabled="loading"
            color="primary"
            variant="flat"
            prepend-icon="mdi-check"
          >
            Confirm & Add Product
          </v-btn>
        </v-sheet>
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" top>
          {{ snackbar.message }}
          <template v-slot:action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
          </template>
        </v-snackbar>
      </form>
      <p v-else>Loading...</p>
    </div>
  </v-container>
</template>

<style scoped>
.cancel {
  background-color: #b61431;
  color: white !important;
}
.container {
  max-width: 1200px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}
.v-text-field {
  width: 100%;
}
.text-red-500 {
  color: red;
}
li {
  list-style: none;
}
.sticky-actions {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 10px 12px;
  z-index: 3;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
